import { APIEntityType } from '@ardoq/api-types';
import { EntityRepresentationProps } from '../../DiffMergeTable/EntityRepresentation/EntityRepresentationProps';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

export const getEntityDataCorruptionType = ({
  entityId,
  parentEntityId,
  entityType,
  enhancedScopeData,
}: Pick<
  EntityRepresentationProps,
  'entityId' | 'entityType' | 'parentEntityId' | 'enhancedScopeData'
>): EntityDataCorruptionType => {
  if (!entityType) {
    return EntityDataCorruptionType.ENTITY_TYPE_MISSING;
  }

  if (entityType === APIEntityType.COMPONENT) {
    return getComponentDataCorruptionType({
      entityId,
      enhancedScopeData,
    });
  }

  if (entityType === APIEntityType.REFERENCE) {
    return getReferenceDataCorruptionType({
      entityId,
      enhancedScopeData,
    });
  }

  if (entityType === APIEntityType.COMPONENT_TYPE) {
    return getComponentTypeDataCorruptionType({
      entityId,
      parentEntityId,
      enhancedScopeData,
    });
  }

  if (entityType === APIEntityType.REFERENCE_TYPE) {
    return getReferenceTypeDataCorruptionType({
      entityId,
      parentEntityId,
      enhancedScopeData,
    });
  }

  return EntityDataCorruptionType.NONE;
};

export const getDataCorruptionDescription = (
  entityDataCorruptionType: EntityDataCorruptionType,
  i18nLocale: I18nLocale = DEFAULT_ARDOQ_I18NLOCALE
) => {
  const entry = entityDataCorruptionTypeToDescriptionMap.get(
    entityDataCorruptionType
  );
  if (!entry) return undefined;
  return t(i18nLocale, `renderers:${entry.key}`, entry.defaultText);
};

export enum EntityDataCorruptionType {
  NONE = 'NONE',
  ENTITY_TYPE_MISSING = 'ENTITY_TYPE_MISSING',
  COMPONENT_IS_MISSING_WORKSPACE = 'COMPONENT_IS_MISSING_WORKSPACE',
  COMPONENT_IS_MISSING_MODEL = 'COMPONENT_IS_MISSING_MODEL',
  COMPONENT_IS_MISSING_TYPE = 'COMPONENT_IS_MISSING_TYPE',
  REFERENCE_IS_MISSING_WORKSPACE = 'REFERENCE_IS_MISSING_WORKSPACE',
  REFERENCE_IS_MISSING_MODEL = 'REFERENCE_IS_MISSING_MODEL',
  REFERENCE_IS_MISSING_TYPE = 'REFERENCE_IS_MISSING_TYPE',
  COMPONENT_TYPE_IS_MISSING_MODEL = 'COMPONENT_TYPE_IS_MISSING_MODEL',
  REFERENCE_TYPE_IS_MISSING_MODEL = 'REFERENCE_TYPE_IS_MISSING_MODEL',
  COMPONENT_IS_MISSING = 'COMPONENT_IS_MISSING',
}

const getComponentDataCorruptionType = ({
  entityId,
  enhancedScopeData,
}: Pick<EntityRepresentationProps, 'entityId' | 'enhancedScopeData'>) => {
  const { componentsById, workspacesById, modelsById, typesByModelId } =
    enhancedScopeData;
  if (!componentsById[entityId]) {
    return EntityDataCorruptionType.COMPONENT_IS_MISSING;
  }
  const { rootWorkspace, typeId } = componentsById[entityId];
  const workspace = workspacesById[rootWorkspace];
  const modelId = workspace?.componentModel;

  if (!workspace)
    return EntityDataCorruptionType.COMPONENT_IS_MISSING_WORKSPACE;
  if (!modelsById[modelId])
    return EntityDataCorruptionType.COMPONENT_IS_MISSING_MODEL;
  if (!typesByModelId[modelId]?.componentTypesById[typeId])
    return EntityDataCorruptionType.COMPONENT_IS_MISSING_TYPE;
  return EntityDataCorruptionType.NONE;
};

const getReferenceDataCorruptionType = ({
  entityId,
  enhancedScopeData,
}: Pick<EntityRepresentationProps, 'entityId' | 'enhancedScopeData'>) => {
  const { referencesById, workspacesById, modelsById, typesByModelId } =
    enhancedScopeData;
  const { rootWorkspace, type: typeId } = referencesById[entityId];
  const workspace = workspacesById[rootWorkspace];
  const modelId = workspace?.componentModel;

  if (!workspace)
    return EntityDataCorruptionType.REFERENCE_IS_MISSING_WORKSPACE;
  if (!modelsById[modelId])
    return EntityDataCorruptionType.REFERENCE_IS_MISSING_MODEL;
  if (!typesByModelId[modelId]?.referenceTypesById[typeId])
    return EntityDataCorruptionType.REFERENCE_IS_MISSING_TYPE;

  return EntityDataCorruptionType.NONE;
};

const getComponentTypeDataCorruptionType = ({
  // entityId,
  parentEntityId,
  enhancedScopeData,
}: Pick<
  EntityRepresentationProps,
  'entityId' | 'parentEntityId' | 'enhancedScopeData'
>) => {
  const { modelsById } = enhancedScopeData;
  if (!parentEntityId || !modelsById[parentEntityId])
    return EntityDataCorruptionType.COMPONENT_TYPE_IS_MISSING_MODEL;
  return EntityDataCorruptionType.NONE;
};

const getReferenceTypeDataCorruptionType = ({
  // entityId,
  parentEntityId,
  enhancedScopeData,
}: Pick<
  EntityRepresentationProps,
  'entityId' | 'parentEntityId' | 'enhancedScopeData'
>) => {
  const { modelsById } = enhancedScopeData;
  if (!parentEntityId || !modelsById[parentEntityId])
    return EntityDataCorruptionType.REFERENCE_TYPE_IS_MISSING_MODEL;
  return EntityDataCorruptionType.NONE;
};

type CorruptionEntry = { key: string; defaultText: string };

const entityDataCorruptionTypeToDescriptionMap = new Map<
  EntityDataCorruptionType,
  CorruptionEntry
>([
  [
    EntityDataCorruptionType.ENTITY_TYPE_MISSING,
    {
      key: 'dataCorruption.entityTypeMissing',
      defaultText: 'Entity type is missing',
    },
  ],
  [
    EntityDataCorruptionType.COMPONENT_IS_MISSING_TYPE,
    {
      key: 'dataCorruption.componentIsMissingType',
      defaultText: 'Component is missing type',
    },
  ],
  [
    EntityDataCorruptionType.COMPONENT_IS_MISSING_WORKSPACE,
    {
      key: 'dataCorruption.componentIsMissingWorkspace',
      defaultText: 'Component is missing workspace',
    },
  ],
  [
    EntityDataCorruptionType.COMPONENT_IS_MISSING_MODEL,
    {
      key: 'dataCorruption.componentIsMissingModel',
      defaultText: 'Component is missing model',
    },
  ],
  [
    EntityDataCorruptionType.REFERENCE_IS_MISSING_TYPE,
    {
      key: 'dataCorruption.referenceIsMissingType',
      defaultText: 'Reference is missing type',
    },
  ],
  [
    EntityDataCorruptionType.REFERENCE_IS_MISSING_WORKSPACE,
    {
      key: 'dataCorruption.referenceIsMissingWorkspace',
      defaultText: 'Reference is missing workspace',
    },
  ],
  [
    EntityDataCorruptionType.REFERENCE_IS_MISSING_MODEL,
    {
      key: 'dataCorruption.referenceIsMissingModel',
      defaultText: 'Reference is missing model',
    },
  ],
  [
    EntityDataCorruptionType.COMPONENT_TYPE_IS_MISSING_MODEL,
    {
      key: 'dataCorruption.componentTypeIsMissingModel',
      defaultText:
        'Component type is unknown because its model cannot be found',
    },
  ],
  [
    EntityDataCorruptionType.REFERENCE_TYPE_IS_MISSING_MODEL,
    {
      key: 'dataCorruption.referenceTypeIsMissingModel',
      defaultText:
        'Reference type is unknown because its model cannot be found',
    },
  ],
  [
    EntityDataCorruptionType.COMPONENT_IS_MISSING,
    {
      key: 'dataCorruption.componentIsMissing',
      defaultText: 'Component is missing from data set',
    },
  ],
]);
